import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent } from 'react';
import { api } from '../lib/api';
import { getState, setState } from '../lib/store';
import { navigate, useLocation } from '../lib/router';
import { useDesktop } from '../lib/useMedia';
import { Icon } from '../components/Icon';
import { BrandLabel, Wordmark } from '../components/Logo';

declare const __PREVIEW__: boolean;
declare const __DEMO__: boolean;
declare const __API_MODE__: 'mock' | 'http';

const DEMO_LOGINS: [string, string][] = [['9000000001', 'Free user'], ['9000000002', 'Plus member'], ['9000000003', 'New user (starts fresh)']];

export function Login() {
  const { query } = useLocation();
  const next = query.get('next') || '/home';
  const desktop = useDesktop();
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wait, setWait] = useState(0);
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (wait <= 0) return;
    const t = window.setTimeout(() => setWait(wait - 1), 1000);
    return () => window.clearTimeout(t);
  }, [wait]);

  const clean = phone.replace(/\D/g, '');
  const validPhone = /^[6-9]\d{9}$/.test(clean);

  const send = async (channel: 'sms' | 'whatsapp' = 'sms') => {
    setError(null);
    if (!validPhone) { setError('Enter a 10-digit Indian mobile number.'); return; }
    setBusy(true);
    try {
      const r = await api.sendOtp(clean, channel);
      setWait(r.resendInS);
      setStep('otp');
      setDigits(['', '', '', '', '', '']);
      window.setTimeout(() => boxes.current[0]?.focus(), 50);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code = digits.join('')) => {
    setError(null);
    // The button is never disabled: if digits are missing, say so and put the cursor on the first empty box.
    const empty = digits.findIndex((x) => !x);
    if (code.length < 6 && empty >= 0) {
      window.setTimeout(() => setError('Enter all 6 digits of the code.'), 0);
      boxes.current[empty]?.focus();
      return;
    }
    setBusy(true);
    try {
      const u = await api.verifyOtp(clean, code);
      setState({ session: { userId: u.userId, phone: clean, name: u.name } });
      navigate(getState().onboarded ? next : `/onboarding?next=${encodeURIComponent(next)}`, { replace: true });
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  const setDigit = (i: number, v: string) => {
    const typed = v.replace(/\D/g, '');
    const copy = [...digits];
    if (typed.length > 1) {
      // Several digits at once (SMS autofill, keyboard suggestion, fast typing): spread them across the boxes.
      const fresh = typed.length >= 6 ? typed.slice(-6) : typed.replace(digits[i] || '', '') || typed;
      fresh.split('').forEach((c, k) => { if (i + k < 6) copy[i + k] = c; });
      setDigits(copy);
      boxes.current[Math.min(i + fresh.length, 5)]?.focus();
    } else {
      copy[i] = typed;
      setDigits(copy);
      if (typed && i < 5) boxes.current[i + 1]?.focus();
    }
    if (copy.every(Boolean)) verify(copy.join(''));
  };
  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) boxes.current[i - 1]?.focus();
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (p.length === 6) { e.preventDefault(); setDigits(p.split('')); verify(p); }
  };

  const form = (
    <div className="auth-card">
      {step === 'phone' ? (
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); send(); }} className="stack">
          <div>
            <h1 className="auth-title">Log in or sign up</h1>
            <p className="muted">We will send a code to your mobile number.</p>
          </div>
          <label htmlFor="phone" className="label">Mobile number</label>
          <div className={'phone-field' + (error ? ' has-error' : '')}>
            <span className="cc">+91</span>
            <input
              id="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="98765 43210"
              value={phone}
              maxLength={11}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d ]/g, ''))}
              aria-describedby={error ? 'auth-error' : undefined}
            />
          </div>
          {error && <p id="auth-error" className="field-error">{error}</p>}
          <button type="submit" className="btn btn-lg btn-primary btn-block" disabled={busy}><span key={busy ? 'b' : 'i'} className="btn-label">{busy ? 'Sending…' : 'Send OTP'}</span></button>
          {__DEMO__ && (
            <div className="demo-note demo-logins">
              <p className="demo-head"><b>Demo logins</b> · tap one, then use OTP <b>123456</b></p>
              {DEMO_LOGINS.map(([n, label]) => (
                <button key={n} type="button" className="text-link" onClick={() => { setPhone(n); setError(null); }}>{n.replace(/(\d{5})(\d{5})/, '$1 $2')} · {label}</button>
              ))}
            </div>
          )}
          {__API_MODE__ === 'mock' && <>
            <div className="or"><span>or</span></div>
            <button type="button" className="btn btn-lg btn-ghost btn-block" onClick={() => { setPhone('9876543210'); setError(null); }}>
              Continue with Google
            </button>
          </>}
          <p className="fine">By continuing, you agree to our Terms and Privacy Policy. Kuch Naya will never call you to ask for your OTP.</p>
        </form>
      ) : (
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); verify(); }} className="stack">
          <button type="button" className="text-link back-link" onClick={() => { setStep('phone'); setError(null); }}>
            <Icon name="back" size={16} /> Change number
          </button>
          <div>
            <h1 className="auth-title">Enter OTP</h1>
            <p className="muted">We sent a 6-digit code to +91 {clean.slice(0, 5)} {clean.slice(5)}.</p>
          </div>
          <fieldset className={'otp' + (error ? ' shake' : '')} aria-describedby={error ? 'auth-error' : undefined}>
            <legend className="sr-only">6-digit code</legend>
            {digits.map((d, i) => (
              <input
                key={i}
                id={`otp-${i}`}
                ref={(el) => { boxes.current[i] = el; }}
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                value={d}
                aria-label={`Digit ${i + 1}`}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKey(i, e)}
                onPaste={onPaste}
              />
            ))}
          </fieldset>
          {error && <p id="auth-error" className="field-error">{error}</p>}
          {__PREVIEW__ && <p className="demo-note">Preview: any 6 digits work. 000000 shows the wrong-code message.</p>}
          <p className="muted small">
            {wait > 0 ? <>Resend code in <b>0:{String(wait).padStart(2, '0')}</b></> : <button type="button" className="text-link" onClick={() => send('sms')}>Resend code</button>}
            {' · '}
            <button type="button" className="text-link" onClick={() => send('whatsapp')}>Get it on WhatsApp</button>
          </p>
          <button type="submit" className="btn btn-lg btn-primary btn-block" aria-busy={busy}><span key={busy ? 'b' : 'i'} className="btn-label">{busy ? 'Checking…' : 'Verify and continue'}</span></button>
          <div className="safety"><Icon name="lock" size={18} /> Kuch Naya will never call you to ask for your OTP. Do not share this code with anyone.</div>
        </form>
      )}
    </div>
  );

  if (desktop) {
    return (
      <div className="auth-split">
        <section className="auth-brand">
          <span className="auth-brand-logo"><Wordmark size={44} dark /></span>
          <div>
            <p className="auth-tag" lang="hi">देखो कुछ नया हर रोज़</p>
            <h2>Something new to watch every day</h2>
            <ul>
              <li>Micro dramas, comedy and college stories</li>
              <li>Continue on any device where you left off</li>
              <li>Useful shows on money, jobs and more</li>
              <li>Free episodes in every show</li>
            </ul>
          </div>
          <small className="auth-foot"><BrandLabel /> © 2026 · A Paper2Pixel company</small>
        </section>
        <section className="auth-panel">{form}</section>
      </div>
    );
  }
  return (
    <div className="auth-phone">
      <div className="auth-phone-top">
        <button type="button" className="text-link muted" onClick={() => navigate('/home')}>Skip for now</button>
      </div>
      <div className="auth-phone-brand">
        <span className="pop-word"><Wordmark size={52} /></span>
        <span className="muted" lang="hi">देखो कुछ नया हर रोज़</span>
      </div>
      {form}
    </div>
  );
}
